import type { ReactNode } from 'react';

import { fade, palette } from '../../common/colors';
import { Alert } from '../../components/Alert/Alert';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { darkThemeSelector, keyframes, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';

const blinkLight = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 2px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
});

const blinkDark = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 1px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
});

const waveLight = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
  '33%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 2px currentColor)',
  },
  '66%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',
  },
});

const waveDark = keyframes({
  '0%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
  '33%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 2px currentColor) drop-shadow(0 0 4px currentColor)',
  },
  '50%': {
    opacity: '0.1',
    filter: 'drop-shadow(0 0 1px currentColor)',
  },
  '66%': {
    opacity: '0.8',
    filter: 'drop-shadow(0 0 2px currentColor) drop-shadow(0 0 4px currentColor)',
  },
  '100%': {
    opacity: '1',
    filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
  },
});

const pulseLengthMS = 300;

const waveLengthMS = 3000;
const waveSegmentMS = waveLengthMS / 3;
const WAVE_DELAYS = [0, waveSegmentMS, waveSegmentMS * 2] as const;

const LEDsLED = styled('div', {
  display: 'flex',
  width: '$10',
  height: '$10',
  borderRadius: '99em',

  variants: {
    color: {
      attention: {
        backgroundColor: fade(palette.amber500, 0.9),
        color: fade(palette.amber500, 0.3),
        strokeAll: fade(palette.amber200, 0.4),

        [darkThemeSelector]: {
          backgroundColor: fade(palette.amber500, 0.9),
          color: fade(palette.amber500, 0.3),
          strokeAll: fade(palette.amber200, 0.4),
        },
      },
      negative: {
        backgroundColor: fade(palette.red500, 0.9),
        color: fade(palette.red500, 0.3),
        strokeAll: fade(palette.red200, 0.4),

        [darkThemeSelector]: {
          backgroundColor: fade(palette.red500, 0.9),
          color: fade(palette.red500, 0.3),
          strokeAll: fade(palette.red200, 0.4),
        },
      },
      neutral: {
        backgroundColor: fade(palette.white, 0.9),
        color: fade(palette.white, 0.3),
        strokeAll: fade(palette.gray200, 0.4),

        [darkThemeSelector]: {
          backgroundColor: fade(palette.white, 0.9),
          color: fade(palette.white, 0.3),
          strokeAll: fade(palette.gray200, 0.4),
        },
      },
      positive: {
        backgroundColor: fade(palette.green500, 0.9),
        color: fade(palette.green500, 0.3),
        strokeAll: fade(palette.green200, 0.4),

        [darkThemeSelector]: {
          backgroundColor: fade(palette.green500, 0.9),
          color: fade(palette.green500, 0.3),
          strokeAll: fade(palette.green200, 0.4),
        },
      },
    },
    animation: {
      none: {
        filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 12px currentColor)',

        [darkThemeSelector]: {
          filter: 'drop-shadow(0 0 4px currentColor) drop-shadow(0 0 8px currentColor)',
        },
      },
      blink: {
        animation: `${blinkLight} ${pulseLengthMS}ms step-end infinite`,
        willChange: 'opacity, transform',

        [darkThemeSelector]: {
          animationName: blinkDark,
        },
      },
      wave: {
        animation: `${waveLight} ${waveLengthMS}ms ease-in-out infinite`,
        willChange: 'opacity, transform',

        [darkThemeSelector]: {
          animationName: waveDark,
        },
      },
    },
    animationDelay: {
      0: { animationDelay: '0ms' },
      [Math.round(waveSegmentMS)]: { animationDelay: `${Math.round(waveSegmentMS)}ms` },
      [Math.round(waveSegmentMS * 2)]: { animationDelay: `${Math.round(waveSegmentMS * 2)}ms` },
    },
  },
});
LEDsLED.displayName = 'LEDsLED';

const LEDsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$3',
  padding: '$4',
  borderRadius: '$6',
  background: fade(palette.tokenBgNeutralLight, 0.5),

  [darkThemeSelector]: {
    background: fade(palette.tokenBgNeutralDark, 0.35),
  },
});
LEDsContainer.displayName = 'LEDsContainer';

export type LEDsState =
  | 'booting'
  | 'initializing'
  | 'management'
  | 'ip'
  | 'registered'
  | 'backend-lost'
  | 'configured'
  | 'resetting'
  | 'rpc';

const stateConfig: Record<
  LEDsState,
  { label: ReactNode; getAnimationProps: (ledIndex: number) => any }
> = {
  booting: {
    label: 'Booting',
    getAnimationProps: (ledIndex) => ({
      color: 'neutral',
      animation: 'wave',
      animationDelay: Math.round(ledIndex * waveSegmentMS) as (typeof WAVE_DELAYS)[number],
    }),
  },
  configured: {
    label: 'Configured',
    getAnimationProps: () => ({ color: 'neutral', animation: 'none' }),
  },
  initializing: {
    label: 'Initializing',
    getAnimationProps: (ledIndex) => ({
      color: ledIndex === 1 ? 'attention' : 'negative',
      animation: 'none',
    }),
  },
  management: {
    label: 'Management link',
    getAnimationProps: (ledIndex) => {
      let color: 'negative' | 'neutral';
      if (ledIndex === 0 || ledIndex === 2) {
        color = 'negative';
      } else {
        color = 'neutral';
      }
      return {
        color,
        animation: 'none',
      };
    },
  },
  ip: {
    label: 'IP received',
    getAnimationProps: (ledIndex) => ({
      color: ledIndex === 2 ? 'negative' : 'neutral',
      animation: 'none',
    }),
  },
  registered: {
    label: 'Registration done',
    getAnimationProps: (ledIndex) => ({
      color: ledIndex === 2 ? 'positive' : 'neutral',
      animation: 'none',
    }),
  },
  'backend-lost': {
    label: 'Backend connection lost',
    getAnimationProps: (ledIndex) => {
      let color: 'attention' | 'neutral';
      if (ledIndex === 0 || ledIndex === 1) {
        color = 'neutral';
      } else {
        color = 'attention';
      }
      return {
        color,
        animation: 'none',
      };
    },
  },
  resetting: {
    label: 'Resetting',
    getAnimationProps: () => ({
      color: 'attention',
      animation: 'blink',
    }),
  },
  rpc: {
    label: 'RPC',
    getAnimationProps: () => ({ color: 'positive', animation: 'blink' }),
  },
};

function LEDsGroup({ state = 'configured' }: { state: LEDsState }) {
  const config = stateConfig[state];

  return (
    <LEDsContainer>
      <LEDsLED {...config.getAnimationProps(0)} />
      <LEDsLED {...config.getAnimationProps(1)} />
      <LEDsLED {...config.getAnimationProps(2)} />
    </LEDsContainer>
  );
}

const LEDsTooltipContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$10',
  widthAll: '$220',
  padding: '$8',
});
LEDsTooltipContainer.displayName = 'LEDsTooltipContainer';

function LEDsTooltip() {
  return (
    <LEDsTooltipContainer>
      <Alert
        type="inline"
        icon="information"
        variant="neutral"
        copy="Illustrative purposes only, doesn't reflect actual device status."
      />
      {Object.entries(stateConfig).map(([stateKey, config]) => (
        <AlignStack
          key={`atto-leds-tooltip-${stateKey}`}
          align="start"
          gap={8}
          preset="small"
          start={<LEDsGroup state={stateKey as LEDsState} />}
        >
          <Text weight="bold">{config.label}</Text>
        </AlignStack>
      ))}
    </LEDsTooltipContainer>
  );
}

export function LEDs({ state = 'configured' }: { state?: LEDsState }) {
  return (
    <Tooltip asChild={false} tight contents={<LEDsTooltip />}>
      <LEDsGroup state={state} />
    </Tooltip>
  );
}
